import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { redirectToStripeCheckout, STRIPE_PAYMENT_LINKS } from '../services/stripeService';

const { FiX, FiCheckCircle, FiZap, FiExternalLink, FiAlertCircle, FiShield } = FiIcons;

function UpgradeModal({ isOpen, onClose }) {
  const { user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isConfigured =
    STRIPE_PAYMENT_LINKS.pro_monthly !== 'https://buy.stripe.com/test_placeholder_monthly' &&
    STRIPE_PAYMENT_LINKS.pro_annual !== 'https://buy.stripe.com/test_placeholder_annual';

  const handleStripeCheckout = async () => {
    if (!isConfigured) {
      setError(
        'Stripe Payment Links are not yet configured. Please add your Payment Link URLs in src/services/stripeService.js'
      );
      return;
    }

    try {
      setLoading(true);
      setError('');
      const plan = billingCycle === 'monthly' ? 'pro_monthly' : 'pro_annual';
      redirectToStripeCheckout(plan, user?.email, user?.id);
    } catch (err) {
      console.error('Stripe checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const proPrice =
    billingCycle === 'monthly'
      ? { amount: '$12', period: '/month' }
      : { amount: '$99', period: '/year', savings: 'Save 30%' };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-space-grotesk font-bold text-secondary mb-1">
                  Upgrade to Pro
                </h2>
                <p className="text-gray-600 text-sm">Secure payment powered by Stripe</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            {/* Not Configured Warning */}
            {!isConfigured && (
              <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-800 mb-1">
                      Stripe Payment Links Required
                    </p>
                    <p className="text-xs text-yellow-700">
                      To accept real payments, create Payment Links in your{' '}
                      <a
                        href="https://dashboard.stripe.com/payment-links"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline font-semibold"
                      >
                        Stripe Dashboard
                      </a>{' '}
                      and update the URLs in <code className="bg-yellow-100 px-1 rounded">src/services/stripeService.js</code>.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Stripe Badge */}
            <div className="flex items-center space-x-2 mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <p className="text-xs text-blue-800">
                <strong>Secure Checkout:</strong> You'll be redirected to Stripe's hosted checkout page. We never store your card details.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Billing Toggle */}
            <div className="mb-6 flex justify-center">
              <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-full font-medium text-sm transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 rounded-full font-medium text-sm transition-all flex items-center space-x-2 ${
                    billingCycle === 'annual'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <span>Annual</span>
                  <span className="text-xs font-bold text-green-600">Save 30%</span>
                </button>
              </div>
            </div>

            {/* Price */}
            <div className="text-center mb-8">
              <div className="flex items-baseline justify-center mb-2">
                <span className="text-5xl font-bold text-primary">{proPrice.amount}</span>
                <span className="text-gray-500 ml-2">{proPrice.period}</span>
              </div>
              {proPrice.savings && (
                <div className="mb-2">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                    {proPrice.savings}
                  </span>
                </div>
              )}
              {billingCycle === 'annual' && (
                <p className="text-sm text-gray-500">That's just $8.25/month, billed annually</p>
              )}
            </div>

            {/* Features */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4">What's Included:</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Unlimited College CRM & Coach tracking',
                  'Full timeline with deadlines',
                  'Invite Parents/Guardians',
                  'Advanced stats & analytics',
                  'Public athlete resume',
                  'Video highlights integration',
                  'PDF resume download',
                  'Priority support',
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleStripeCheckout}
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold text-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Redirecting to Stripe...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiZap} className="w-5 h-5" />
                  <span>Subscribe with Stripe</span>
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="mt-4 text-center space-y-1">
              <p className="text-xs text-gray-500">Cancel anytime • No hidden fees • Secure payment</p>
              <p className="text-xs text-gray-400">
                By subscribing, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>

            {/* Stripe Trust Badges */}
            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-center space-x-4">
              <div className="flex items-center space-x-1 text-gray-400">
                <SafeIcon icon={FiShield} className="w-3 h-3" />
                <span className="text-xs">SSL Encrypted</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-400">
                <span className="text-xs">💳</span>
                <span className="text-xs">Powered by Stripe</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-400">
                <span className="text-xs">🛡️</span>
                <span className="text-xs">PCI Compliant</span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default UpgradeModal;